import { useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconExclamationMark,
  IconLock,
} from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
} from "../../../../../gql/__generated__/graphql";
import { CHANGE_PASSWORD } from "../../../../apis/lambda/GraphQL/Email/mutations";

interface ChangePasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

function ChangePasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const resetPasswordToken = searchParams.get("resetPasswordToken");

  const form = useForm<ChangePasswordFormValues>({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validate: {
      newPassword: (value) => {
        if (!value) return "New password is required";
        if (value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.newPassword) return "Passwords do not match";
        return null;
      },
    },
  });

  const [changePassword, { loading }] = useMutation<
    ChangePasswordMutation,
    ChangePasswordMutationVariables
  >(CHANGE_PASSWORD, {
    onCompleted: () => {
      notifications.show({
        title: "Password changed",
        message:
          "Your password has been successfully updated. Redirecting to login...",
        color: "green",
      });
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    },
    onError: (error) => {
      notifications.show({
        title: "Failed to change password.",
        message: `Reason: ${error.message}. Please contact us if you believe there is an error.`,
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    },
  });

  function handleSubmit(values: ChangePasswordFormValues) {
    if (!email) {
      notifications.show({
        title: "Error",
        message: "Email is missing.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
      return;
    }
    if (!resetPasswordToken) {
      notifications.show({
        title: "Error",
        message: "Missing confirmation token.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
      return;
    }
    changePassword({
      variables: {
        confirmationToken: resetPasswordToken,
        password: values.newPassword,
        email: email,
      },
    }).catch(() => {
      // Error is handled by the onError callback in useMutation
    });
  }

  // Show error state when required parameters are missing
  if (!email || !resetPasswordToken) {
    return (
      <Box
        style={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container size="xs" w="100%">
          <Stack gap="xl" align="stretch" w="100%">
            <Title
              order={1}
              ta="center"
              style={{
                background:
                  "linear-gradient(45deg, var(--mantine-color-tertiary-6), var(--mantine-color-yellow-6))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                lineHeight: 1.3,
              }}
            >
              Invalid Reset Link
            </Title>

            <Paper p="xl" radius="lg" shadow="md" withBorder>
              <Stack gap="lg" align="center">
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  title="Link Invalid or Expired"
                  color="red"
                  variant="light"
                  w="100%"
                >
                  <Text size="sm">
                    This password reset link is invalid or has expired. This can
                    happen if:
                  </Text>
                  <Text size="sm" mt="xs">
                    • The link is older than the allowed time limit
                  </Text>
                  <Text size="sm">• The link has already been used</Text>
                  <Text size="sm">• The link was copied incorrectly</Text>
                </Alert>

                <Text ta="center" c="dimmed" size="sm">
                  Please request a new password reset link to continue.
                </Text>

                <Stack gap="sm" w="100%">
                  <Button
                    size="lg"
                    fullWidth
                    color="orange"
                    onClick={() => navigate("/login/forgot-password")}
                  >
                    Request New Reset Link
                  </Button>

                  <Button
                    size="lg"
                    fullWidth
                    variant="light"
                    color="gray"
                    onClick={() => navigate("/login")}
                  >
                    Back to Login
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container size="xs" w="100%">
        <Stack gap="xl" align="stretch" w="100%">
          <Title
            order={1}
            ta="center"
            style={{
              background:
                "linear-gradient(45deg, var(--mantine-color-tertiary-6), var(--mantine-color-yellow-6))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.3,
            }}
          >
            Change Password
          </Title>

          <Paper p="xl" radius="lg" shadow="md" withBorder>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="lg">
                <PasswordInput
                  label="New Password"
                  placeholder="Enter your new password"
                  leftSection={<IconLock size={16} />}
                  {...form.getInputProps("newPassword")}
                />
                <PasswordInput
                  label="Confirm New Password"
                  placeholder="Confirm your new password"
                  leftSection={<IconLock size={16} />}
                  {...form.getInputProps("confirmPassword")}
                />

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  color="orange"
                  loading={loading}
                >
                  Change Password
                </Button>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default ChangePasswordPage;
